import { Component, Input, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { NbDialogRef, NbToastrService } from '@nebular/theme';
import { TranslateService } from '@ngx-translate/core';
import {
  AvvStatus,
  OrganisationService,
} from '../../../services/organisation.service';

@Component({
  selector: 'app-avv-dialog',
  templateUrl: './avv-dialog.component.html',
  styleUrl: './avv-dialog.component.scss',
})
export class AvvDialogComponent implements OnInit {
  @Input() data?: AvvStatus;

  form: FormGroup;
  saving = false;
  downloading = false;
  avv?: AvvStatus;
  renderedPreviewHtml = '';

  constructor(
    protected fb: FormBuilder,
    protected dialogRef: NbDialogRef<AvvDialogComponent>,
    protected organisationService: OrganisationService,
    protected toastr: NbToastrService,
    protected translate: TranslateService
  ) {
    this.form = this.fb.group({
      customer_name: this.fb.control('', Validators.required),
      customer_street: this.fb.control('', Validators.required),
      customer_number: this.fb.control('', Validators.required),
      customer_zip: this.fb.control('', Validators.required),
      customer_city: this.fb.control('', Validators.required),
      customer_country: this.fb.control(''),
      accepted: this.fb.control(false, Validators.requiredTrue),
    });
  }

  ngOnInit(): void {
    this.setAvv(this.data);
    if (this.data) {
      this.form.patchValue({
        customer_name: this.data.customer_name || '',
        customer_street: this.data.customer_street || '',
        customer_number: this.data.customer_number || '',
        customer_zip: this.data.customer_zip || '',
        customer_city: this.data.customer_city || '',
        customer_country: this.data.customer_country || '',
        accepted: this.data.accepted || false,
      });
    }
  }

  showInputError(name: string) {
    return this.form.get(name)?.touched && this.form.get(name)?.invalid;
  }

  private setAvv(status?: AvvStatus): void {
    this.avv = status;
    this.renderedPreviewHtml = this.renderMarkdown(status?.preview_markdown || '');
  }

  private escapeHtml(value: string): string {
    return value
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');
  }

  private cleanInlineMarkdown(value: string): string {
    return value
      .replace(/\\([\\`*_{}\[\]()#+.!-])/g, '$1')
      .replace(/\\-/g, '-')
      .replace(/\\\./g, '.')
      .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
      .replace(/__(.*?)__/g, '<strong>$1</strong>')
      .replace(/\*(.*?)\*/g, '<em>$1</em>');
  }

  private renderInline(value: string): string {
    return this.cleanInlineMarkdown(this.escapeHtml(value.trim()));
  }

  private isTableSeparator(line: string): boolean {
    return /^\|?\s*:?-{3,}:?\s*(\|\s*:?-{3,}:?\s*)+\|?$/.test(line);
  }

  private tableCells(line: string): string[] {
    return line
      .trim()
      .replace(/^\|/, '')
      .replace(/\|$/, '')
      .split('|')
      .map((cell) => cell.trim());
  }

  private renderTable(lines: string[]): string {
    if (lines.length < 2) return '';
    const headers = this.tableCells(lines[0]);
    const rows = lines.slice(2).map((line) => this.tableCells(line));

    return [
      '<table>',
      '<thead><tr>',
      headers.map((header) => `<th>${this.renderInline(header)}</th>`).join(''),
      '</tr></thead>',
      '<tbody>',
      rows
        .map((row) => {
          const cells = headers.map((_, index) => row[index] || '');
          return `<tr>${cells.map((cell) => `<td>${this.renderInline(cell)}</td>`).join('')}</tr>`;
        })
        .join(''),
      '</tbody></table>',
    ].join('');
  }

  private renderMarkdown(markdown: string): string {
    if (!markdown) return '';

    const lines = markdown.split(/\r?\n/);
    const html: string[] = [];
    let paragraph: string[] = [];
    let orderedListOpen = false;
    let unorderedListOpen = false;

    const flushParagraph = () => {
      if (!paragraph.length) return;
      html.push(`<p>${paragraph.join('<br>')}</p>`);
      paragraph = [];
    };

    const closeLists = () => {
      if (orderedListOpen) {
        html.push('</ol>');
        orderedListOpen = false;
      }
      if (unorderedListOpen) {
        html.push('</ul>');
        unorderedListOpen = false;
      }
    };

    for (let index = 0; index < lines.length; index += 1) {
      const raw = lines[index];
      const line = raw.trim();

      if (!line) {
        flushParagraph();
        closeLists();
        continue;
      }

      if (
        line.startsWith('|') &&
        lines[index + 1] &&
        this.isTableSeparator(lines[index + 1].trim())
      ) {
        flushParagraph();
        closeLists();
        const tableLines = [line, lines[index + 1].trim()];
        index += 2;
        while (index < lines.length && lines[index].trim().startsWith('|')) {
          tableLines.push(lines[index].trim());
          index += 1;
        }
        index -= 1;
        html.push(this.renderTable(tableLines));
        continue;
      }

      const heading = line.match(/^(#{1,3})\s+(.+)$/);
      if (heading) {
        flushParagraph();
        closeLists();
        const level = heading[1].length + 1;
        html.push(`<h${level}>${this.renderInline(heading[2])}</h${level}>`);
        continue;
      }

      const boldHeading = line.match(/^\*\*(.+)\*\*$/);
      if (boldHeading) {
        flushParagraph();
        closeLists();
        html.push(`<h3>${this.renderInline(boldHeading[1])}</h3>`);
        continue;
      }

      const ordered = line.match(/^(\d+)\\?\.\s*(.+)$/);
      if (ordered) {
        flushParagraph();
        if (unorderedListOpen) {
          html.push('</ul>');
          unorderedListOpen = false;
        }
        if (!orderedListOpen) {
          html.push('<ol>');
          orderedListOpen = true;
        }
        html.push(`<li>${this.renderInline(ordered[2])}</li>`);
        continue;
      }

      const unordered = line.match(/^(?:[-*•])\s+(.+)$/);
      if (unordered) {
        flushParagraph();
        if (orderedListOpen) {
          html.push('</ol>');
          orderedListOpen = false;
        }
        if (!unorderedListOpen) {
          html.push('<ul>');
          unorderedListOpen = true;
        }
        html.push(`<li>${this.renderInline(unordered[1])}</li>`);
        continue;
      }

      closeLists();
      paragraph.push(this.renderInline(line.replace(/\s{2,}$/, '')));
    }

    flushParagraph();
    closeLists();
    return html.join('');
  }

  acceptAndDownload(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    this.saving = true;
    this.organisationService
      .acceptAvv({
        customer_name: this.form.value.customer_name,
        customer_street: this.form.value.customer_street,
        customer_number: this.form.value.customer_number,
        customer_zip: this.form.value.customer_zip,
        customer_city: this.form.value.customer_city,
        customer_country: this.form.value.customer_country || null,
        accepted: true,
      })
      .subscribe({
        next: (status) => {
          this.saving = false;
          this.setAvv(status);
          this.toastr.success(
            this.translate.instant('pages.organisation.avv.acceptedToast'),
            ''
          );
          this.download();
        },
        error: () => {
          this.saving = false;
          this.toastr.danger(
            this.translate.instant('pages.organisation.avv.acceptError'),
            ''
          );
        },
      });
  }

  download(): void {
    this.downloading = true;
    this.organisationService.downloadAvv().subscribe({
      next: (blob) => {
        this.downloading = false;
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `alphabees-avv-${this.avv?.version || '1.0'}.pdf`;
        link.click();
        URL.revokeObjectURL(url);
      },
      error: () => {
        this.downloading = false;
        this.toastr.danger(
          this.translate.instant('pages.organisation.avv.downloadError'),
          ''
        );
      },
    });
  }
}
